/**
 * /home — Home Card Stack (Phase 3.4 — Escape Hatch + Haptics + Confetti).
 *
 * Phase 3.1: data layer + static visual.
 * Phase 3.2: right-swipe (Setuju) + undo + a11y buttons.
 * Phase 3.3: left-swipe (reject) + reason sheet + auto-swap.
 * Phase 3.4: escape hatch + haptics + confetti.
 *   - "Biar Kami Pilih" → POST /v1/daily/:child_id/auto-approve.
 *   - "Lewati hari ini" → POST /v1/daily/:child_id/skip. No streak penalty.
 *   - expo-haptics: medium impact on right swipe, light on left swipe,
 *     success notification on auto-approve, warning on skip.
 *   - Confetti burst (custom particle animation, 500ms) on every
 *     successful approve — right swipe atau auto-approve.
 * Phase 3.6 (UX simplification + kuota baru):
 *   - Swipe kiri = reject LANGSUNG (log + kartu pengganti), tanpa sheet
 *     wajib. Alasan reject jadi banner opsional non-blocking
 *     (RejectReasonBanner → POST .../swipe/:id/reason).
 *   - Approve dibatasi preferensi dailyCount (1-5/hari), 2 putaran rekomendasi
 *     (5 kartu awal + pengganti s.d. 10 tawaran/putaran, maks 20 baru/hari),
 *     plus review pool untuk worksheet yang dilewati.
 * Phase 3.7 (swipe-only, 2026-07-29):
 *   - Tombol Setuju / Ganti (A11yButtons) dihapus — interaksi murni
 *     swipe gesture. A11yButtons.tsx ikut dihapus.
 *
 * Deferrals:
 *   - 14 PAUD tema enrichment (product-knowledge-dev JOIN)
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { ActivityIndicator, Animated, Easing, Pressable, StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import * as Haptics from 'expo-haptics';

import { ThemedText } from '@/components/themed-text';
import { ThemedView } from '@/components/themed-view';
import { AppIcon } from '@/components/app-icon';
import { UserAvatar } from '@/components/main/UserAvatar';
import { Brand, Colors, Radii, Spacing } from '@/constants/theme';
import { SwipeStack, type SwipeStackHandle } from '@/components/home/SwipeStack';
import { UndoButton } from '@/components/home/UndoButton';
import { RejectReasonBanner } from '@/components/home/RejectReasonBanner';
import { CreditPaywallCard } from '@/components/home/CreditPaywallCard';
import { PremiumUpgradeModal } from '@/components/premium/PremiumUpgradeModal';
import { Confetti, type ConfettiHandle } from '@/components/home/Confetti';
import { EmptyView, ErrorView, LoadingView } from '@/components/home/StateViews';
import { HomeTour } from '@/components/home/HomeTour';
import { MainPageHeader } from '@/components/main/MainPageHeader';
import { ConfirmationModal } from '@/components/library/ConfirmationModal';
import { Toast } from '@/components/library/Toast';
import { TrialWall } from '@/components/trial-wall';
import { getActiveChild } from '@/lib/api/children';
import {
  getDailyOffer,
  getDailyReview,
  regenerateDailyOffer,
  postSwipe,
  postSwipeReason,
  postUndo,
  postAutoApprove,
  postSkip,
  type PostSwipeResult,
} from '@/lib/api/daily';
import { ApiError } from '@/lib/api/client';
import { getStoredAccount, isGuestSession } from '@/lib/auth';
import { trackAnalyticsEvent } from '@/lib/analytics';
import { greeting } from '@/lib/greeting';
import { greetingIcon, MISC_ICON } from '@/lib/icons';
import { tierForSession } from '@/lib/premium';
import {
  getPremiumPromptState,
  setPremiumPromptState,
  shouldShowPremiumReminder,
} from '@/lib/premium-prompt';
import { ONBOARDING_ROUTES } from '@/constants/onboarding';

import type { ChildProfile, RejectReason } from '@funpaper/shared';
import type { DailyStack, WorksheetCard } from '@/types/daily';

type HomeState =
  | { kind: 'loading' }
  | { kind: 'ready'; child: ChildProfile; stack: DailyStack }
  | { kind: 'empty'; child: ChildProfile; message: string }
  | { kind: 'error'; message: string };

type ActiveUndo = {
  swipeLogId: number;
  undoExpiresAt: string;
  direction: 'approve' | 'reject';
  restoreIndex?: number;
  card?: WorksheetCard;
  replacementWorksheetId?: number | null;
};

/**
 * Phase 3.6: state untuk banner alasan opsional. Dibuat tepat setelah
 * reject swipe berhasil di-log (swipeLogId sudah ada); ditutup saat user
 * memilih alasan, tap ✕, atau swipe kartu berikutnya.
 */
type ReasonPrompt = {
  swipeLogId: number;
  cardName: string;
};

export default function HomeScreen() {
  const router = useRouter();
  const { refresh } = useLocalSearchParams<{ refresh?: string }>();
  const [state, setState] = useState<HomeState>({ kind: 'loading' });
  const [cards, setCards] = useState<WorksheetCard[]>([]);
  const [cardIndex, setCardIndex] = useState(0);
  const [approvedCount, setApprovedCount] = useState(0);
  const [activeUndo, setActiveUndo] = useState<ActiveUndo | null>(null);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [undoing, setUndoing] = useState(false);
  const [autoApproving, setAutoApproving] = useState(false);
  const [skipping, setSkipping] = useState(false);
  const [skipped, setSkipped] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [reasonPrompt, setReasonPrompt] = useState<ReasonPrompt | null>(null);
  const [savingReason, setSavingReason] = useState(false);
  const [reviewMode, setReviewMode] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [trialExpired, setTrialExpired] = useState(false);
  const [creditPaywall, setCreditPaywall] = useState<{ cost: number; balance: number } | null>(null);

  const swiperRef = useRef<SwipeStackHandle>(null);
  const confettiRef = useRef<ConfettiHandle>(null);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const viewedOfferKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!creditPaywall) return;
    trackAnalyticsEvent('Paywall Viewed', {
      required_credits: creditPaywall.cost,
      credit_balance: creditPaywall.balance,
    });
  }, [creditPaywall]);

  useEffect(() => {
    if (state.kind !== 'ready') return;
    const offerKey = `${state.child.id}:${state.stack.offerId ?? state.stack.offerDate}`;
    if (viewedOfferKeyRef.current === offerKey) return;
    viewedOfferKeyRef.current = offerKey;
    trackAnalyticsEvent('Daily Recommendations Viewed', {
      cards_count: state.stack.cards.length,
      recommendation_turn: state.stack.turnsUsed,
      cached: state.stack.isCached,
      limit_reached: state.stack.limitReached,
      premium: state.stack.premium,
    });
  }, [state]);

  // --------------------------------------------------------------------------
  // Initial load
  // --------------------------------------------------------------------------

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let child: ChildProfile | null = null;
      try {
        child = await getActiveChild();
      } catch {
        if (!cancelled) {
          router.replace(ONBOARDING_ROUTES.story as never);
        }
        return;
      }
      if (cancelled) return;
      if (!child) {
        router.replace(ONBOARDING_ROUTES.story as never);
        return;
      }
      try {
        const stack = await getDailyOffer(child.id);
        if (cancelled) return;
        setReviewMode(false);
        if (stack.limitReached) {
          setState({ kind: 'ready', child, stack });
          setCards([]);
          setCardIndex(0);
          setApprovedCount(stack.approvedCount);
        } else if (stack.count === 0 && !stack.canRegenerate && !stack.reviewAvailable) {
          setState({
            kind: 'empty',
            child,
            message: stack.message ?? 'Worksheet belum tersedia. Coba lagi nanti.',
          });
        } else {
          setState({ kind: 'ready', child, stack });
          setCards(stack.cards);
          setCardIndex(0);
          setApprovedCount(stack.approvedCount);
        }
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.code === 'TrialExpired') {
          setTrialExpired(true);
          return;
        }
        const message =
          err instanceof ApiError
            ? err.message
            : 'Gagal memuat worksheet hari ini. Cek koneksimu.';
        setState({ kind: 'error', message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [router, refresh]);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  // --------------------------------------------------------------------------
  // Toast helper
  // --------------------------------------------------------------------------

  const showToast = useCallback((message: string, durationMs = 1500) => {
    setToast(message);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => {
      setToast(null);
      toastTimerRef.current = null;
    }, durationMs);
  }, []);

  // --------------------------------------------------------------------------
  // Haptics wrapper — expo-haptics throws on web (no native module), so
  // each call is fire-and-forget with a swallowed rejection.
  // --------------------------------------------------------------------------

  const haptic = useCallback((style: 'light' | 'medium' | 'success' | 'warning') => {
    try {
      if (style === 'success') {
        Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success).catch(() => {});
      } else if (style === 'warning') {
        Haptics.notificationAsync(Haptics.NotificationFeedbackType.Warning).catch(() => {});
      } else if (style === 'medium') {
        Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium).catch(() => {});
      } else {
        Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light).catch(() => {});
      }
    } catch {
      // expo-haptics itself not available — ignore.
    }
  }, []);

  const triggerConfetti = useCallback(() => {
    confettiRef.current?.play();
  }, []);

  // --------------------------------------------------------------------------
  // Swipe right (approve)
  // --------------------------------------------------------------------------

  const handleSwipedRight = useCallback(
    async (idx: number) => {
      if (state.kind !== 'ready') return;
      if (approving || rejecting) {
        swiperRef.current?.swipeBack();
        return;
      }
      const approveLimit = state.stack.approveLimit;
      if (approvedCount >= approveLimit) {
        swiperRef.current?.swipeBack();
        showToast(`Maksimal ${approveLimit} worksheet per hari.`);
        return;
      }
      const card = cards[idx];
      if (!card) {
        swiperRef.current?.swipeBack();
        return;
      }

      setApproving(true);
      // Advance the visual deck immediately; persistence may take a moment.
      setCardIndex(idx + 1);
      // Swipe baru = banner alasan (dari reject sebelumnya) tidak relevan lagi.
      setReasonPrompt(null);
      let result: PostSwipeResult | null = null;
      try {
        result = await postSwipe({
          childProfileId: state.child.id,
          worksheetId: card.worksheetId,
          direction: 'approve',
          offerId: state.stack.offerId,
        });
      } catch (err) {
        setCardIndex(idx);
        swiperRef.current?.swipeBack();
        if (err instanceof ApiError && err.code === 'TrialExpired') {
          setTrialExpired(true);
          return;
        }
        if (err instanceof ApiError && err.code === 'CreditRequired') {
          // Saldo habis → kartu paywall dengan CTA langsung ke top-up
          // (dulu hanya toast sesaat tanpa jalan keluar).
          const body = (err.body ?? {}) as { balance?: number; cost?: number };
          setCreditPaywall({ cost: body.cost ?? 1, balance: body.balance ?? 0 });
          return;
        }
        const message =
          err instanceof ApiError
            ? err.message
            : 'Gagal menyimpan. Coba lagi.';
        showToast(message, 2500);
        return;
      } finally {
        setApproving(false);
      }

      trackAnalyticsEvent('Worksheet Decision', {
        action: 'approve',
        position: card.position,
        source: 'swipe',
        pick_source: result.pickSource ?? 'unknown',
      });
      trackAnalyticsEvent('Worksheet Saved', {
        source: 'swipe',
        position: card.position,
        pick_source: result.pickSource ?? 'unknown',
      });
      setApprovedCount((n) => n + 1);
      setState((previous) => previous.kind === 'ready'
        ? {
            ...previous,
            stack: {
              ...previous.stack,
              turnsUsed: result.turnsUsed,
              offersShown: result.offersShown,
              approveLimit: result.approveLimit,
              canRegenerate: result.canRegenerate ?? false,
              reviewAvailable: result.reviewAvailable,
            },
          }
        : previous);
      setActiveUndo({
        swipeLogId: result.swipeLogId,
        undoExpiresAt: result.undoExpiresAt,
        direction: 'approve',
      });
      haptic('medium');
      triggerConfetti();
      showToast('Worksheet dipilih!');
    },
    [state, approving, rejecting, approvedCount, cards, showToast, haptic, triggerConfetti],
  );

  // --------------------------------------------------------------------------
  // Swipe left (reject) — Phase 3.6: langsung log + ganti kartu.
  // Alasan reject pindah ke banner opsional (handlePickReason di bawah).
  // --------------------------------------------------------------------------

  const handleSwipedLeft = useCallback(
    async (idx: number) => {
      if (state.kind !== 'ready') return;
      if (approving || rejecting) {
        // A swipe is in flight; ignore this one.
        swiperRef.current?.swipeBack();
        return;
      }
      const card = cards[idx];
      if (!card) {
        swiperRef.current?.swipeBack();
        return;
      }

      haptic('light');
      setReasonPrompt(null);
      setRejecting(true);
      // Keep the next card visible while the reject + replacement request runs.
      setCardIndex(idx + 1);
      try {
        const result = await postSwipe({
          childProfileId: state.child.id,
          worksheetId: card.worksheetId,
          direction: 'reject',
          offerId: state.stack.offerId,
        });
        trackAnalyticsEvent('Worksheet Decision', {
          action: 'reject',
          position: card.position,
          source: reviewMode ? 'review' : 'recommendation',
        });
        if (result.newWorksheet) {
          setCards((prev) => [...prev, result.newWorksheet!]);
        }
        setActiveUndo({
          swipeLogId: result.swipeLogId,
          undoExpiresAt: result.undoExpiresAt,
          direction: 'reject',
          restoreIndex: idx,
          card,
          replacementWorksheetId: result.newWorksheet?.worksheetId ?? null,
        });
        setState((previous) => previous.kind === 'ready'
          ? {
              ...previous,
              stack: {
                ...previous.stack,
                turnsUsed: result.turnsUsed,
                offersShown: result.offersShown,
                canRegenerate: result.canRegenerate ?? false,
                reviewAvailable: result.reviewAvailable,
              },
            }
          : previous);
        // Banner alasan opsional — tidak blocking, bisa diabaikan.
        setReasonPrompt({ swipeLogId: result.swipeLogId, cardName: card.name });
        if (result.newWorksheet) {
          showToast('Worksheet diganti');
        } else if (result.canRegenerate) {
          showToast('Putaran ini selesai. Kamu masih punya 1 putaran rekomendasi.', 2500);
        } else if (result.offerCapReached) {
          showToast('Semua rekomendasi baru hari ini sudah dilihat.', 2500);
        } else {
          showToast(reviewMode ? 'Worksheet dilewati lagi.' : 'Semua worksheet di putaran ini sudah dilihat.', 2500);
        }
      } catch (err) {
        // Network error. Rewind the card visually.
        setCardIndex(idx);
        swiperRef.current?.swipeBack();
        if (err instanceof ApiError && err.code === 'TrialExpired') {
          setTrialExpired(true);
          return;
        }
        const message =
          err instanceof ApiError
            ? err.message
            : 'Gagal mengganti worksheet. Coba lagi.';
        showToast(message, 2500);
      } finally {
        setRejecting(false);
      }
    },
    [state, approving, rejecting, cards, reviewMode, showToast, haptic],
  );

  // --------------------------------------------------------------------------
  // Banner alasan opsional (Phase 3.6) — backfill reason ke swipe_log row
  // yang sudah tercatat. Gagal = silent (alasan hanya analytics).
  // --------------------------------------------------------------------------

  const handlePickReason = useCallback(
    async (reason: RejectReason) => {
      if (state.kind !== 'ready' || !reasonPrompt) return;
      const { swipeLogId } = reasonPrompt;
      setSavingReason(true);
      try {
        await postSwipeReason({
          childProfileId: state.child.id,
          swipeLogId,
          rejectReason: reason,
        });
        showToast('Terima kasih! Masukanmu membantu');
      } catch {
        // Non-critical — reason opsional, jangan ganggu flow swipe.
      } finally {
        setSavingReason(false);
        setReasonPrompt(null);
      }
    },
    [state, reasonPrompt, showToast],
  );

  const handleDismissReason = useCallback(() => {
    setReasonPrompt(null);
  }, []);

  // --------------------------------------------------------------------------
  // Recommendation turn + explicit skipped-card review.
  // --------------------------------------------------------------------------

  const handleRegenerate = useCallback(async () => {
    if (state.kind !== 'ready' || regenerating || !state.stack.canRegenerate) return;
    setRegenerating(true);
    try {
      const stack = await regenerateDailyOffer(state.child.id);
      setState({ kind: 'ready', child: state.child, stack });
      setCards(stack.cards);
      setCardIndex(0);
      setReviewMode(false);
      setReasonPrompt(null);
      showToast(
        stack.cards.length > 0
          ? 'Rekomendasi baru siap.'
          : stack.message ?? 'Belum ada worksheet baru yang cocok.',
        2500,
      );
    } catch (err) {
      if (err instanceof ApiError && err.code === 'TrialExpired') {
        setTrialExpired(true);
        return;
      }
      const message = err instanceof ApiError ? err.message : 'Gagal memuat rekomendasi baru.';
      showToast(message, 2500);
    } finally {
      setRegenerating(false);
    }
  }, [state, regenerating, showToast]);

  const handleReview = useCallback(async () => {
    if (state.kind !== 'ready' || reviewLoading || !state.stack.reviewAvailable) return;
    setReviewLoading(true);
    try {
      const reviewCards = await getDailyReview(state.child.id);
      if (reviewCards.length === 0) {
        showToast('Tidak ada worksheet yang perlu ditinjau.', 2000);
        return;
      }
      setCards(reviewCards);
      setCardIndex(0);
      setReviewMode(true);
      setReasonPrompt(null);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'TrialExpired') {
        setTrialExpired(true);
        return;
      }
      const message = err instanceof ApiError ? err.message : 'Gagal memuat worksheet yang dilewati.';
      showToast(message, 2500);
    } finally {
      setReviewLoading(false);
    }
  }, [state, reviewLoading, showToast]);

  // --------------------------------------------------------------------------
  // Escape hatch: "Biar Kami Pilih" (auto-approve all remaining)
  // --------------------------------------------------------------------------

  const handleAutoApprove = useCallback(async () => {
    if (state.kind !== 'ready') return;
    if (autoApproving) return;
    if (approvedCount >= state.stack.approveLimit) {
      showToast('Sudah mencapai batas harian.');
      return;
    }
    setAutoApproving(true);
    try {
      const result = await postAutoApprove(state.child.id);
      for (const worksheetId of result.approvedWorksheetIds) {
        const card = cards.find((candidate) => candidate.worksheetId === worksheetId);
        const properties = {
          source: 'auto_approve',
          position: card?.position ?? null,
        };
        trackAnalyticsEvent('Worksheet Decision', { action: 'approve', ...properties });
        trackAnalyticsEvent('Worksheet Saved', properties);
      }
      setApprovedCount((n) => n + result.approvedCount);
      setState((previous) => previous.kind === 'ready'
        ? { ...previous, stack: { ...previous.stack, approveLimit: result.cap } }
        : previous);
      if (result.approvedCount > 0) {
        const approvedIds = new Set(result.approvedWorksheetIds);
        const remainingCards = cards.filter((card) => !approvedIds.has(card.worksheetId));
        setCards(remainingCards);
        setCardIndex((index) => Math.min(index, remainingCards.length));
        setActiveUndo(null);
      }
      haptic('success');
      // Confetti at most once per escape-hatch tap, regardless of how
      // many cards were approved.
      triggerConfetti();
      if (result.approvedCount === 0) {
        showToast(result.message ?? 'Tidak ada worksheet yang bisa dipilih.', 2000);
      } else {
        if (result.creditRequired) {
          // Auto-approve berhenti karena saldo habis → tawarkan top-up langsung.
          setCreditPaywall(result.creditRequired);
        }
        showToast(
          result.creditRequired
            ? `${result.approvedCount} worksheet dipilih. Saldo point habis.`
            : `${result.approvedCount} worksheet dipilih!`,
          2000,
        );
      }
    } catch (err) {
      if (err instanceof ApiError && err.code === 'TrialExpired') {
        setTrialExpired(true);
        return;
      }
      const message =
        err instanceof ApiError
          ? err.message
          : 'Gagal auto-approve. Coba lagi.';
      showToast(message, 2500);
    } finally {
      setAutoApproving(false);
    }
  }, [state, autoApproving, approvedCount, cards, showToast, haptic, triggerConfetti]);

  // --------------------------------------------------------------------------
  // Escape hatch: "Lewati hari ini" (skip day)
  // --------------------------------------------------------------------------

  const handleSkip = useCallback(async () => {
    if (state.kind !== 'ready') return;
    if (skipping) return;
    setSkipping(true);
    try {
      await postSkip(state.child.id);
      haptic('warning');
      setSkipped(true);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'TrialExpired') {
        setTrialExpired(true);
        return;
      }
      const message =
        err instanceof ApiError
          ? err.message
          : 'Gagal melewati hari ini. Coba lagi.';
      showToast(message, 2500);
      setSkipping(false);
    }
  }, [state, skipping, showToast, haptic]);

  // --------------------------------------------------------------------------
  // Undo (right-swipe approve)
  // --------------------------------------------------------------------------

  const handleUndo = useCallback(async () => {
    if (state.kind !== 'ready') return;
    if (undoing) return;
    if (!activeUndo) return;

    const previousIndex = Math.max(0, cardIndex - 1);

    setUndoing(true);
    try {
      await postUndo({
        childProfileId: state.child.id,
        swipeLogId: activeUndo.swipeLogId,
      });
      if (activeUndo.direction === 'approve') {
        swiperRef.current?.swipeBack();
        setApprovedCount((n) => Math.max(0, n - 1));
        setCardIndex(previousIndex);
      } else if (activeUndo.card) {
        const restoredCard = activeUndo.card;
        const restoreIndex = activeUndo.restoreIndex ?? previousIndex;
        setCards((prev) => {
          const withoutReplacement = activeUndo.replacementWorksheetId
            ? prev.filter((item) => item.worksheetId !== activeUndo.replacementWorksheetId)
            : prev;
          if (withoutReplacement.some((item) => item.worksheetId === restoredCard.worksheetId)) {
            return withoutReplacement;
          }
          return [
            ...withoutReplacement.slice(0, restoreIndex),
            restoredCard,
            ...withoutReplacement.slice(restoreIndex),
          ];
        });
        swiperRef.current?.jumpToCardIndex(restoreIndex);
        setCardIndex(restoreIndex);
      }
      setActiveUndo(null);
      showToast('Dibatalkan.');
    } catch (err) {
      if (err instanceof ApiError && err.code === 'UndoWindowExpired') {
        setActiveUndo(null);
        showToast('Waktu batal sudah habis.');
      } else if (err instanceof ApiError && err.code === 'AlreadyUndone') {
        setActiveUndo(null);
        showToast('Sudah dibatalkan.');
      } else {
        const message =
          err instanceof ApiError ? err.message : 'Gagal membatalkan.';
        showToast(message, 2500);
      }
    } finally {
      setUndoing(false);
    }
  }, [state, undoing, activeUndo, cardIndex, showToast]);

  // --------------------------------------------------------------------------
  // Render
  // --------------------------------------------------------------------------

  if (trialExpired) {
    return (
      <ThemedView style={styles.root}>
        <TrialWall visible={trialExpired} />
      </ThemedView>
    );
  }
  if (state.kind === 'loading') return <LoadingView />;
  if (state.kind === 'empty') return <EmptyView child={state.child} message={state.message} />;
  if (state.kind === 'error') return <ErrorView message={state.message} />;
  if (skipped) {
    return <SkippedHome child={state.child} />;
  }
  // Reminder preferensi premium — dihitung saat render (murah, selalu segar).
  const premiumReminder = shouldShowPremiumReminder({
    isPremium: state.kind === 'ready' ? state.stack.premium : false,
    promptState: getPremiumPromptState(getStoredAccount()?.id ?? null),
  });
  return (
    <ReadyHome
      child={state.child}
      stack={state.stack}
      premiumReminder={premiumReminder}
      cards={cards}
      cardIndex={cardIndex}
      approvedCount={approvedCount}
      activeUndo={activeUndo}
      undoing={undoing}
      approving={approving}
      rejecting={rejecting}
      savingReason={savingReason}
      autoApproving={autoApproving}
      skipping={skipping}
      reviewMode={reviewMode}
      regenerating={regenerating}
      reviewLoading={reviewLoading}
      confettiRef={confettiRef}
      swiperRef={swiperRef}
      onSwipedRight={handleSwipedRight}
      onSwipedLeft={handleSwipedLeft}
      onUndo={handleUndo}
      reasonPrompt={reasonPrompt}
      onPickReason={handlePickReason}
      onDismissReason={handleDismissReason}
      creditPaywall={creditPaywall}
      onTopUpFromPaywall={() => {
        setCreditPaywall(null);
        router.push('/belanja/top-up' as never);
      }}
      onDismissPaywall={() => setCreditPaywall(null)}
      onAutoApprove={handleAutoApprove}
      onSkip={handleSkip}
      onRegenerate={handleRegenerate}
      onReview={handleReview}
      toast={toast}
    />
  );
}

// ----------------------------------------------------------------------------
// SkippedHome — full-screen "Sampai besok" view after the user taps
// "Lewati hari ini". No escape (plan §8.2 says skip is a one-way
// choice for the day; tomorrow brings a fresh stack).
// ----------------------------------------------------------------------------

function SkippedHome({ child }: { child: ChildProfile }) {
  return (
    <ThemedView style={styles.root}>
      <SafeAreaView style={styles.safe} edges={['top', 'left', 'right', 'bottom']}>
        <View style={styles.skippedCenter}>
          <AppIcon source={MISC_ICON.books} size={72} />
          <ThemedText type="subtitle" style={styles.skippedTitle}>
            Sampai besok!
          </ThemedText>
          <ThemedText
            type="default"
            themeColor="textSecondary"
            style={styles.skippedBody}
          >
            Kamu sudah melewati hari ini. Besok kami siapkan worksheet
            baru untuk {child.name}.
          </ThemedText>
        </View>
      </SafeAreaView>
    </ThemedView>
  );
}

// ----------------------------------------------------------------------------
// ReadyHome
// ----------------------------------------------------------------------------

type ReadyHomeProps = {
  child: ChildProfile;
  stack: DailyStack;
  /** Fase 4 plan 23: reminder preferensi premium (premium + dismissed). */
  premiumReminder: boolean;
  cards: WorksheetCard[];
  cardIndex: number;
  approvedCount: number;
  activeUndo: ActiveUndo | null;
  undoing: boolean;
  approving: boolean;
  rejecting: boolean;
  savingReason: boolean;
  autoApproving: boolean;
  skipping: boolean;
  reviewMode: boolean;
  regenerating: boolean;
  reviewLoading: boolean;
  confettiRef: React.RefObject<ConfettiHandle | null>;
  swiperRef: React.RefObject<SwipeStackHandle | null>;
  onSwipedRight: (idx: number) => void;
  onSwipedLeft: (idx: number) => void;
  onUndo: () => void;
  reasonPrompt: ReasonPrompt | null;
  onPickReason: (reason: RejectReason) => void;
  onDismissReason: () => void;
  creditPaywall: { cost: number; balance: number } | null;
  onTopUpFromPaywall: () => void;
  onDismissPaywall: () => void;
  onAutoApprove: () => void;
  onSkip: () => void;
  onRegenerate: () => void;
  onReview: () => void;
  toast: string | null;
};

function ReadyHome({
  child,
  stack,
  premiumReminder,
  cards,
  cardIndex,
  approvedCount,
  activeUndo,
  undoing,
  approving,
  rejecting,
  savingReason,
  autoApproving,
  skipping,
  reviewMode,
  regenerating,
  reviewLoading,
  confettiRef,
  swiperRef,
  onSwipedRight,
  onSwipedLeft,
  onUndo,
      reasonPrompt,
      onPickReason,
      onDismissReason,
      creditPaywall,
      onTopUpFromPaywall,
      onDismissPaywall,
      onAutoApprove,
  onSkip,
  onRegenerate,
  onReview,
  toast,
}: ReadyHomeProps) {
  const g = greeting();
  const router = useRouter();
  const [escapeAction, setEscapeAction] = useState<'auto' | 'skip' | null>(null);
  const [reminderOpen, setReminderOpen] = useState(false);
  // Fungsi biasa (bukan useCallback) — React Compiler menolak memoization
  // dengan dep setReminderOpen yang tidak tercantum di array.
  const onReminderChoose = () => {
    setReminderOpen(false);
    // "Pilih Aktivitas" = user sudah bertindak — matikan badge reminder.
    setPremiumPromptState(getStoredAccount()?.id ?? null, 'completed');
    router.push('/akun/edit-preferensi' as never);
  };
  const [escapeMenuOpen, setEscapeMenuOpen] = useState(false);
  const [escapeFabProgress] = useState(() => new Animated.Value(0));

  useEffect(() => {
    Animated.timing(escapeFabProgress, {
      toValue: escapeMenuOpen ? 1 : 0,
      duration: 180,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    }).start();
  }, [escapeFabProgress, escapeMenuOpen]);

  const approveLimit = stack.approveLimit;
  const atCap = approvedCount >= approveLimit;
  const deckExhausted = cards.length === 0 || cardIndex >= cards.length;
  const remainingCards = Math.max(0, cards.length - cardIndex);
  const turnLabel = `Putaran ${Math.min(stack.turnsUsed, stack.maxTurns)} dari ${stack.maxTurns}`;

  return (
    <ThemedView style={styles.root}>
      <SafeAreaView style={styles.safe} edges={['top', 'left', 'right']}>
        <MainPageHeader
          title={g.text}
          titleAccessory={<AppIcon source={greetingIcon(g.timeOfDay)} size={20} />}
          subtitle={
            atCap
              ? `Semua worksheet untuk ${child.name} sudah dipilih`
              : reviewMode
                ? `Meninjau worksheet yang dilewati untuk ${child.name}`
                : `${remainingCards} worksheet tersisa · ${turnLabel}`
          }
          right={(
            <View style={styles.topBarRight}>
              {activeUndo ? (
                <UndoButton
                  expiresAt={activeUndo.undoExpiresAt}
                  onUndo={onUndo}
                  loading={undoing}
                />
              ) : null}
              <UserAvatar
                initial={child.name.charAt(0)}
                tier={tierForSession({ isGuest: isGuestSession(), isPremium: stack.premium })}
              />
            </View>
          )}
          style={styles.topBar}
        />

        {premiumReminder ? (
          <View style={styles.premiumReminderWrap}>
            <Pressable
              onPress={() => setReminderOpen(true)}
              style={({ pressed }) => [styles.premiumBadge, pressed && styles.premiumBadgePressed]}
              accessibilityRole="button"
              accessibilityLabel="Lengkapi preferensi premium"
            >
              <ThemedText style={styles.premiumBadgeGlyph}>✨</ThemedText>
              <ThemedText style={styles.premiumBadgeText}>Premium aktif</ThemedText>
              <ThemedText style={styles.premiumBadgeCta}>Lengkapi ›</ThemedText>
            </Pressable>
          </View>
        ) : null}

        <View style={styles.stackArea}>
          {atCap ? (
            // Kuota 5 approve tercapai → deck kosong dengan keterangan,
            // bukan sisa kartu yang tidak bisa dipilih lagi.
            <View style={styles.deckEmpty}>
              <AppIcon source={MISC_ICON.checkmark} size={96} />
              <ThemedText style={styles.deckEmptyTitle}>
                Semua rekomendasi worksheet sudah dipilih hari ini
              </ThemedText>
              <ThemedText
                type="small"
                themeColor="textSecondary"
                style={styles.deckEmptyBody}
              >
                Rekomendasi hari ini sudah mencapai batas {approveLimit} worksheet. Masih butuh aktivitas lain? Lihat pilihan tambahan di Belanja.
              </ThemedText>
              <Pressable
                onPress={() => router.push('/belanja' as never)}
                style={({ pressed }) => [styles.premiumCta, pressed && styles.premiumCtaPressed]}
                accessibilityRole="button"
                accessibilityLabel="Lihat worksheet tambahan"
              >
                <ThemedText style={styles.premiumCtaText}>Lihat worksheet tambahan</ThemedText>
              </Pressable>
            </View>
          ) : deckExhausted ? (
            <View style={styles.deckEmpty}>
              <AppIcon source={reviewMode ? MISC_ICON.books : MISC_ICON.sparkle} size={78} />
              <ThemedText style={styles.deckEmptyTitle}>
                {reviewMode
                  ? 'Tidak ada lagi worksheet yang perlu ditinjau'
                  : stack.canRegenerate
                    ? 'Putaran ini sudah selesai'
                    : 'Semua rekomendasi baru hari ini sudah dilihat'}
              </ThemedText>
              <ThemedText
                type="small"
                themeColor="textSecondary"
                style={styles.deckEmptyBody}
              >
                {stack.canRegenerate
                  ? 'Kamu bisa mencari rekomendasi baru atau meninjau worksheet yang tadi dilewati.'
                  : stack.reviewAvailable
                    ? 'Kamu masih bisa meninjau worksheet yang tadi dilewati.'
                    : `Coba lagi besok untuk rekomendasi baru untuk ${child.name}.`}
              </ThemedText>
              {stack.canRegenerate ? (
                <Pressable
                  onPress={onRegenerate}
                  disabled={regenerating || reviewLoading}
                  style={({ pressed }) => [styles.premiumCta, pressed && styles.premiumCtaPressed]}
                  accessibilityRole="button"
                  accessibilityLabel="Cari rekomendasi baru"
                >
                  <ThemedText style={styles.premiumCtaText}>Cari rekomendasi baru</ThemedText>
                </Pressable>
              ) : null}
              {stack.reviewAvailable ? (
                <Pressable
                  onPress={onReview}
                  disabled={regenerating || reviewLoading}
                  style={({ pressed }) => [styles.reviewCta, pressed && styles.premiumCtaPressed]}
                  accessibilityRole="button"
                  accessibilityLabel="Tinjau worksheet yang dilewati"
                >
                  <ThemedText style={styles.reviewCtaText}>Tinjau yang dilewati</ThemedText>
                </Pressable>
              ) : null}
            </View>
          ) : (
            <SwipeStack
              ref={swiperRef}
              cards={cards}
              cardIndex={cardIndex}
              childName={child.name}
              onSwipedRight={onSwipedRight}
              onSwipedLeft={onSwipedLeft}
              loading={approving || rejecting}
              loadingLabel={rejecting ? 'Mencari worksheet baru…' : 'Menyimpan pilihan…'}
              onSwipedAll={() => {
                // Deck habis → empty state di dalam SwipeStack tampil.
              }}
            />
          )}
          <Confetti ref={confettiRef} />
        </View>

        <View style={styles.bottomArea}>
          {creditPaywall ? (
            <CreditPaywallCard
              cost={creditPaywall.cost}
              balance={creditPaywall.balance}
              onTopUp={onTopUpFromPaywall}
              onDismiss={onDismissPaywall}
            />
          ) : reasonPrompt ? (
            <RejectReasonBanner
              cardName={reasonPrompt.cardName}
              onPick={onPickReason}
              onDismiss={onDismissReason}
              loading={savingReason}
            />
          ) : null}
          <ThemedText type="small" themeColor="textSecondary" style={styles.counterLine}>
            {approvedCount} dari {approveLimit} worksheet dipilih hari ini
          </ThemedText>

        </View>
        <View pointerEvents="box-none" style={styles.escapeFab}>
          {escapeMenuOpen ? (
            <View style={styles.escapeOptions}>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="Pilih semua worksheet otomatis"
                accessibilityHint="Funpaper akan memilih semua worksheet yang tersisa sekaligus sesuai preferensi"
                onPress={() => {
                  setEscapeMenuOpen(false);
                  setEscapeAction('auto');
                }}
                disabled={autoApproving || atCap}
                style={({ pressed }) => [
                  styles.escapeOption,
                  styles.escapeOptionPrimary,
                  (autoApproving || atCap) && styles.escapeBtnDisabled,
                  pressed && !autoApproving && !atCap && styles.escapeBtnPressed,
                ]}
              >
                <AppIcon source={MISC_ICON.autoSelect} size={34} />
              </Pressable>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="Lewati rekomendasi hari ini"
                accessibilityHint="Tidak ada worksheet yang dipilih hari ini"
                onPress={() => {
                  setEscapeMenuOpen(false);
                  setEscapeAction('skip');
                }}
                disabled={skipping}
                style={({ pressed }) => [
                  styles.escapeOption,
                  styles.escapeOptionSecondary,
                  skipping && styles.escapeBtnDisabled,
                  pressed && !skipping && styles.escapeBtnPressed,
                ]}
              >
                <AppIcon source={MISC_ICON.skipDay} size={34} />
              </Pressable>
            </View>
          ) : null}
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={escapeMenuOpen ? 'Tutup pilihan tindakan' : 'Buka pilihan tindakan'}
            accessibilityHint={
              escapeMenuOpen
                ? 'Menutup pilihan tindakan tambahan'
                : 'Tampilkan pilihan memilih otomatis atau melewati hari ini'
            }
            accessibilityState={{ expanded: escapeMenuOpen }}
            onPress={() => setEscapeMenuOpen((open) => !open)}
            style={({ pressed }) => [
              styles.escapeFabMain,
              escapeMenuOpen && styles.escapeFabMainOpen,
              pressed && styles.escapeBtnPressed,
            ]}
          >
            <Animated.View
              style={[
                styles.escapeFabIcon,
                {
                  transform: [
                    {
                      rotate: escapeFabProgress.interpolate({
                        inputRange: [0, 1],
                        outputRange: ['0deg', '45deg'],
                      }),
                    },
                    {
                      scale: escapeFabProgress.interpolate({
                        inputRange: [0, 1],
                        outputRange: [1, 0.9],
                      }),
                    },
                  ],
                },
              ]}
            >
              <AppIcon source={MISC_ICON.actionMenu} size={38} />
            </Animated.View>
          </Pressable>
        </View>
        {autoApproving || skipping || regenerating || reviewLoading ? (
          <View
            pointerEvents="box-only"
            style={styles.actionLoadingOverlay}
            accessibilityRole="progressbar"
            accessibilityLabel={
              autoApproving
                ? 'Memilih worksheet'
                : skipping
                  ? 'Menyiapkan tampilan besok'
                  : regenerating
                    ? 'Mencari rekomendasi baru'
                    : 'Memuat worksheet yang dilewati'
            }
          >
            <ActivityIndicator size="large" color={Brand.primary} />
            <ThemedText type="smallBold" style={styles.actionLoadingText}>
              {autoApproving
                ? 'Memilih worksheet…'
                : skipping
                  ? 'Menyiapkan tampilan besok…'
                  : regenerating
                    ? 'Mencari rekomendasi baru…'
                    : 'Memuat worksheet yang dilewati…'}
            </ThemedText>
          </View>
        ) : null}
      </SafeAreaView>

      <Toast message={toast} />
      <ConfirmationModal
        visible={escapeAction !== null}
        title={escapeAction === 'auto' ? 'Pilih semua worksheet otomatis?' : 'Lewati rekomendasi hari ini?'}
        body={
          escapeAction === 'auto'
            ? 'Funpaper akan langsung memilih semua worksheet yang tersisa hari ini sesuai preferensi, lalu menyimpannya ke aktivitas hari ini.'
            : 'Tidak ada worksheet yang dipilih hari ini. Rekomendasi baru akan tersedia besok.'
        }
        confirmLabel={escapeAction === 'auto' ? 'Ya' : 'Lewati hari ini'}
        cancelLabel="Batal"
        onConfirm={() => {
          const action = escapeAction;
          setEscapeAction(null);
          if (action === 'auto') onAutoApprove();
          if (action === 'skip') onSkip();
        }}
        onCancel={() => setEscapeAction(null)}
      />
      <PremiumUpgradeModal
        visible={reminderOpen}
        title="Lengkapi preferensimu"
        body="Pilihan aktivitas premium bertambah dari 5 menjadi 15. Lengkapi preferensi supaya rekomendasi worksheet makin pas untuk si kecil."
        onChoose={onReminderChoose}
        onLater={() => setReminderOpen(false)}
      />
      {/* Product tour 1x per device: jelaskan konsep swipe-the-card.
          Self-contained — cek flag localStorage sendiri, render null
          kalau sudah pernah tampil. */}
      <HomeTour />
    </ThemedView>
  );
}

// ----------------------------------------------------------------------------
// (Tab bar moved to (main)/_layout.tsx — Phase 3.5)
// ----------------------------------------------------------------------------

// ----------------------------------------------------------------------------
// Styles
// ----------------------------------------------------------------------------

const styles = StyleSheet.create({
  root: { flex: 1 },
  safe: { flex: 1 },
  topBar: {
    paddingHorizontal: Spacing.four,
    backgroundColor: Colors.light.background,
    zIndex: 2,
  },
  topBarRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.two,
  },
  premiumReminderWrap: {
    alignItems: 'center',
    paddingHorizontal: Spacing.four,
    paddingBottom: Spacing.two,
  },
  premiumBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: 'rgba(86, 63, 122, 0.08)',
    borderWidth: 1,
    borderColor: Brand.primary,
    borderRadius: Radii.pill,
    paddingHorizontal: 12,
    paddingVertical: 6,
    alignSelf: 'center',
  },
  premiumBadgePressed: { opacity: 0.8 },
  premiumBadgeGlyph: { fontSize: 11, lineHeight: 14 },
  premiumBadgeText: {
    fontSize: 12,
    lineHeight: 16,
    fontWeight: '600',
    color: Brand.primary,
  },
  premiumBadgeCta: {
    fontSize: 11,
    lineHeight: 14,
    fontWeight: '700',
    color: '#ffffff',
    backgroundColor: Brand.primary,
    borderRadius: Radii.pill,
    paddingHorizontal: 10,
    paddingVertical: 3,
    overflow: 'hidden',
  },
  stackArea: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  deckEmpty: {
    alignItems: 'center',
    gap: Spacing.three,
    paddingHorizontal: Spacing.four,
  },
  deckEmptyTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: Colors.light.text,
    textAlign: 'center',
    maxWidth: 320,
  },
  deckEmptyBody: {
    textAlign: 'center',
    maxWidth: 320,
  },
  premiumCta: {
    marginTop: Spacing.two,
    backgroundColor: Brand.primary,
    paddingVertical: Spacing.two,
    paddingHorizontal: Spacing.four,
    borderRadius: Radii.medium,
  },
  premiumCtaText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '700',
  },
  premiumCtaPressed: {
    opacity: 0.75,
  },
  reviewCta: {
    marginTop: Spacing.one,
    borderWidth: 1,
    borderColor: Brand.primary,
    paddingVertical: Spacing.two,
    paddingHorizontal: Spacing.four,
    borderRadius: Radii.medium,
  },
  reviewCtaText: {
    color: Brand.primary,
    fontSize: 14,
    fontWeight: '700',
  },
  bottomArea: {
    paddingHorizontal: Spacing.four,
    paddingBottom: Spacing.three,
    gap: Spacing.two,
    // Kartu deck diposisikan absolut di dalam stackArea; di layar pendek
    // kartu bisa meluber ke bawah. Background solid + zIndex memastikan
    // counter & tombol selalu di atas kartu, tidak ketumpuk.
    backgroundColor: Colors.light.background,
    zIndex: 2,
  },
  counterLine: {
    textAlign: 'center',
  },
  escapeFab: {
    position: 'absolute',
    right: Spacing.four,
    bottom: Spacing.three,
    alignItems: 'flex-end',
    zIndex: 4,
  },
  escapeOptions: {
    alignItems: 'flex-end',
    gap: Spacing.three,
  },
  escapeOption: {
    width: 58,
    height: 58,
    borderRadius: 29,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.16,
    shadowRadius: 5,
    elevation: 5,
  },
  escapeOptionPrimary: {
    borderWidth: 2,
    borderColor: Brand.primary,
    backgroundColor: '#f0e4ff',
  },
  escapeOptionSecondary: {
    borderWidth: 2,
    borderColor: Brand.accent,
    backgroundColor: '#fff0e4',
  },
  escapeFabMain: {
    width: 58,
    height: 58,
    borderRadius: 29,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Brand.primary,
    borderWidth: 2,
    borderColor: Brand.primaryDark,
    marginTop: Spacing.three,
    shadowColor: '#000000',
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.2,
    shadowRadius: 6,
    elevation: 7,
  },
  escapeFabMainOpen: {
    backgroundColor: Brand.teal,
    borderColor: Brand.teal,
    shadowColor: Brand.teal,
    shadowOpacity: 0.34,
    shadowRadius: 8,
  },
  escapeFabIcon: { alignSelf: 'center' },
  escapeBtnDisabled: {
    opacity: 0.4,
  },
  escapeBtnPressed: {
    opacity: 0.75,
  },
  actionLoadingOverlay: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 8,
    alignItems: 'center',
    justifyContent: 'center',
    gap: Spacing.two,
    backgroundColor: 'rgba(252, 251, 248, 0.88)',
  },
  actionLoadingText: {
    color: Brand.primary,
  },
  skippedCenter: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: Spacing.four,
    gap: Spacing.three,
  },
  skippedTitle: {
    textAlign: 'center',
    color: Brand.primary,
  },
  skippedBody: {
    textAlign: 'center',
    maxWidth: 360,
    lineHeight: 22,
  },
});
